/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useMemo } from 'react';
import { IP_WATERMARK_TEXT, isProtectedSession } from '@/config/ipProtection';
import { useAuth } from '@/lib/auth';

/**
 * Personalised watermark text: for protected sessions (demo / patient / guest)
 * the logged-in identity and the date are appended, so any leaked screenshot
 * is traceable to the account that produced it. Admin and anonymous sessions
 * get the plain proprietary notice.
 */
export const useWatermarkText = (base = IP_WATERMARK_TEXT) => {
  const auth = useAuth();
  const user = auth?.user;
  return useMemo(() => {
    if (!user || !isProtectedSession(user)) return base;
    const who = user.email || user.id || '';
    const day = new Date().toISOString().slice(0, 10);
    return `${base} · Sessione demo: ${who} · ${day}`;
  }, [base, user]);
};

const buildTile = (text) => {
  const safe = String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  // Longer texts get a wider tile so the line never gets cut at the tile edge.
  const w = Math.max(640, Math.min(1400, Math.round(text.length * 7.2)));
  const h = Math.round(w / 2);
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}'>` +
    `<g transform='rotate(-28 ${w / 2} ${h / 2})' fill='currentColor' font-family='Inter, Figtree, ui-sans-serif, sans-serif' font-size='13' font-weight='600' letter-spacing='0.4'>` +
    `<text x='0' y='${Math.round(h * 0.28)}'>${safe}</text>` +
    `<text x='${-Math.round(w / 2)}' y='${Math.round(h * 0.72)}'>${safe}</text>` +
    `</g></svg>`;
  return { image: `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`, size: `${w}px ${h}px` };
};

/**
 * ProprietaryWatermark — subtle repeating diagonal text painted BEHIND the
 * content of its positioned parent (`relative isolate`), or over the whole
 * viewport when `fixed` is set (used for traceable demo sessions).
 *
 * One DOM node, CSS background from an SVG tile, pointer-events disabled and
 * aria-hidden: never interferes with interaction or screen readers.
 */
export const ProprietaryWatermark = ({ text, opacity = 0.055, className = '', fixed = false, testId = 'proprietary-watermark' }) => {
  const personalised = useWatermarkText();
  const finalText = text || personalised;
  const tile = useMemo(() => buildTile(finalText), [finalText]);

  return (
    <div
      aria-hidden="true"
      data-testid={testId}
      data-watermark={finalText}
      className={`pointer-events-none select-none text-foreground print:hidden ${fixed ? 'fixed inset-0 z-[60]' : 'absolute inset-0 -z-10'} ${className}`}
      style={{ backgroundImage: tile.image, backgroundRepeat: 'repeat', backgroundSize: tile.size, opacity }}
    />
  );
};

/**
 * SessionWatermark — full-viewport traceable watermark for protected logged-in
 * sessions (demo / patient / guest). Renders nothing for admins and anonymous
 * visitors (those already get the page-level watermark on login/setup).
 */
export const SessionWatermark = () => {
  const auth = useAuth();
  const user = auth?.user;
  if (!user || !isProtectedSession(user)) return null;
  return <ProprietaryWatermark fixed opacity={0.045} className="session-watermark" testId="session-watermark" />;
};

export default ProprietaryWatermark;
